import { useSessionKey } from "@auth/sessionStore";
import { useRoomDoorLabels } from "@clinicMap/clinicMapQueries";
import { Button, Input } from "@scribenote/brian-design-system";
import { type Ref, useState } from "react";

import type {
  Crossing,
  GroundTruthEntry,
  GroundTruthIn,
  Phase,
} from "./perceptionClient";
import { useGroundTruth, useRecordGroundTruth } from "./perceptionQueries";
import { ago, phaseWord } from "./words";

const SHOWN_ENTRIES = 3;

export const GROUND_TRUTH_KEY = "l";

const CROSSING_WORD: Record<Crossing, string> = {
  out: "Walked out",
  in: "Walked in",
  none: "Didn’t cross",
};

const CROSSINGS = Object.keys(CROSSING_WORD) as Crossing[];

const entryLine = (
  entry: GroundTruthEntry,
  doors: ReadonlyMap<string, string> | undefined,
  now: number,
) =>
  [
    ago(entry.recordedAt, now),
    entry.phase && phaseWord(entry.phase),
    entry.door &&
      `${CROSSING_WORD[entry.door.crossing]} · ${
        doors?.get(entry.door.door_uuid) ?? "a door"
      }`,
    entry.text,
    entry.authorName,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * Ground truth for scoring the room lane (brian `ground_truth`): one tap for the
 * phase the room is in now, or a typed line about what happened. brian times
 * each entry when it arrives, so record it as it happens. With `doorTaps`, each
 * of the room's doors on the clinic map gets a `Crossing` tap (BRI-270). A
 * refused door tap re-reads the map: the door may have been moved or removed.
 * With `at`, each entry is timed by it instead (the video frame on screen).
 */
export const GroundTruth = ({
  roomId,
  active,
  doorTaps,
  now,
  inputRef,
  at,
}: {
  roomId: string;
  active: boolean;
  doorTaps: boolean;
  now: number;
  inputRef: Ref<HTMLInputElement>;
  at?: () => string;
}) => {
  const sessionKey = useSessionKey();
  const truth = useGroundTruth(sessionKey, roomId, active);
  const record = useRecordGroundTruth(sessionKey, roomId);
  const { labels: doors, refetch: rereadMap } = useRoomDoorLabels(
    sessionKey,
    roomId,
    active,
  );
  const [text, setText] = useState("");

  const save = (
    body: GroundTruthIn,
    options?: Parameters<typeof record.mutate>[1],
  ) => record.mutate({ ...body, at: at?.() }, options);

  const send = () => {
    const line = text.trim();
    if (!line || record.isPending) return;
    save({ text: line }, { onSuccess: () => setText("") });
  };

  return (
    <section
      aria-label="What happened"
      className="flex flex-col gap-2 border-t border-rule px-3 py-2"
    >
      {at && (
        <p className="text-sm text-annotation">Recorded at the video’s time.</p>
      )}
      {truth.data && (
        <div className="flex flex-wrap gap-1">
          {truth.data.phases.map((phase: Phase) => (
            <Button
              key={phase}
              variant="outline"
              size="sm"
              disabled={record.isPending}
              onClick={() => save({ phase })}
            >
              {phaseWord(phase)}
            </Button>
          ))}
        </div>
      )}
      {doorTaps && doors && (
        <div role="group" aria-label="Doors" className="flex flex-col gap-1.5">
          <p className="text-sm text-annotation">
            {doors.size === 0
              ? "No doors on the clinic map for this room."
              : "Walked out or in only once someone is all the way through. At the door but stayed: Didn’t cross."}
          </p>
          {[...doors].map(([doorId, leadsTo]) => (
            <div key={doorId} className="flex flex-col gap-0.5">
              <span className="truncate text-sm text-ink">{leadsTo}</span>
              <div className="flex flex-wrap gap-1">
                {CROSSINGS.map((crossing) => (
                  <Button
                    key={crossing}
                    variant="outline"
                    size="sm"
                    disabled={record.isPending}
                    onClick={() =>
                      save(
                        { door: { door_uuid: doorId, crossing } },
                        { onError: () => void rereadMap() },
                      )
                    }
                  >
                    {CROSSING_WORD[crossing]}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <form
        data-dd-privacy="hidden"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <Input
          ref={inputRef}
          aria-label="What happened"
          placeholder="What happened? (L)"
          maxLength={2000}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Escape") return;
            // An unprevented Escape also closes an enclosing modal <dialog>.
            e.preventDefault();
            e.currentTarget.blur();
          }}
        />
      </form>
      {truth.data && truth.data.entries.length > 0 && (
        <ul
          data-dd-privacy="hidden"
          className="flex flex-col gap-0.5 font-mono text-fine-print text-annotation"
        >
          {truth.data.entries.slice(0, SHOWN_ENTRIES).map((entry) => (
            <li key={entry.id} className="truncate">
              {entryLine(entry, doors, now)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
